import React, { useCallback, useState } from 'react'
import { type useForm } from 'react-hook-form'
import { type FieldError } from 'react-hook-form'
import { type DashMessages } from 'intl/intl.types'
import { useFormErrorMessageMap } from 'utils/react-hook-form/reactHookForm.hooks'
import { ReCaptchaCheckBox } from './ReCaptchaCheckBox'

type RegisterFn = ReturnType<typeof useForm>['register']
type SetValueFn = ReturnType<typeof useForm>['setValue']

export interface UseFormReCaptchaCheckboxOptions {
  register: RegisterFn
  setValue: SetValueFn
  action: string
  name?: string
  errorMessageId?: DashMessages
  error: FieldError
}

export const useFormReCaptchaCheckbox = ({
  setValue,
  register,
  action,
  name,
  error,
  errorMessageId,
}: UseFormReCaptchaCheckboxOptions) => {
  const resolvedName = name ?? 'reCaptchaToken'
  const registerReCaptchaTokenField = useCallback(
    () => register({ name: resolvedName }, { required: true }),
    [register, resolvedName]
  )

  const setReCaptchaTokenValue = useCallback(
    (value: string, shouldValidate = false) => {
      setValue(resolvedName, value, shouldValidate)
    },
    [setValue, resolvedName]
  )

  // If we get an error from the API, we want to reset our captcha
  const [reCaptchaResetHandler, setReCaptchaResetHandler] = useState<() => void>(null)

  const resetReCaptcha = useCallback(() => {
    setReCaptchaTokenValue('', false)
    try {
      reCaptchaResetHandler?.()
    } catch (err) {
      /**
       * This is due to some routes dismounting recaptcha, which means the clients might be gone
       */
      console.log(err)
    }
  }, [reCaptchaResetHandler, setReCaptchaTokenValue])

  const reCaptchaTokenErrorMessageId = useFormErrorMessageMap({
    required: errorMessageId ?? 'reCaptcha.error.required',
  })(error)

  const reCaptchaElement = (
    <ReCaptchaCheckBox
      setResetHandler={setReCaptchaResetHandler}
      action={action}
      onLoad={registerReCaptchaTokenField}
      onChange={setReCaptchaTokenValue}
      errorMessageId={reCaptchaTokenErrorMessageId as DashMessages}
    />
  )

  return { reCaptchaElement, resetReCaptcha }
}
